import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import BarChartComponent from './charts/BarChartComponent';
import ChartEditHeader from './charts/ChartEditHeader';
import ConfirmationModal from 'components/common-components/ConfirmationModal';
import DashboardAddWidgetModal from './DashboardAddWidgetModal/DashboardAddWidgetModal';
import ImageChartComponent from './charts/ImageChartComponent';
import LineChartComponent from './charts/LineChartComponent';
import PieChartComponent from './charts/PieChartComponent';
import StatsChartComponent from './charts/StatsChartComponent';
import TextChartComponent from './charts/TextChartComponent';
import { chartTempData, DEFAULT_NUMBER_OF_COLUMNS, findAvailablePosition } from './utils';
import { CallAPI } from 'actions/General';
import HeatMapChartComponent from './charts/HeatMapChartComponent';
import DialChartComponent from './charts/DialChartComponent';

const PageWidgetsEditView = ({
  selectedPageId,
  getPageDetails,
  currentWidgetsRows,
  widgetList,
  setWidgetList,
  dashboardId,
  setSaveLoader,
}) => {
  const { t } = useTranslation();
  const [selectedWidgetId, setSelectedWidgetId] = useState(null);
  const [confirmType, setConfirmType] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const totalOccupiedCells = useMemo(
    () => widgetList.reduce((acc, cur) => acc + cur.takeRows * cur.takeColumns, 0),
    [widgetList],
  );

  const handleDeleteWidget = async () => {
    if (!selectedWidgetId) return;
    const result = await CallAPI('DELETE_DASHBOARD_WIDGET', null, selectedWidgetId, setSaveLoader);
    if (result.status) {
      getPageDetails();
      toast.success(t('alert_success_dashboard_widget_deleted'));
    }
    setConfirmType(null);
    setSelectedWidgetId(null);
  };

  const handleDuplicateWidget = async () => {
    if (!selectedWidgetId) return;

    const widgetToDuplicate = widgetList.find((w) => w._id === selectedWidgetId);
    const widgetCurrentSize = {
      takeRows: widgetToDuplicate.takeRows,
      takeColumns: widgetToDuplicate.takeColumns,
    };
    let availablePosition = findAvailablePosition(
      widgetList,
      widgetCurrentSize,
      currentWidgetsRows + widgetCurrentSize.takeRows,
    );

    if (availablePosition) {
      const result = await CallAPI(
        'DUPLICATE_DASHBOARD_WIDGET',
        {
          ...availablePosition,
          ...widgetCurrentSize,
          widgetId: selectedWidgetId,
        },
        null,
        setSaveLoader,
      );
      if (result.status) {
        getPageDetails();
        toast.success(t('alert_success_dashboard_widget_duplicated'));
      }
    }
    setConfirmType(null);
    setSelectedWidgetId(null);
  };

  const handleAction = async (action, value) => {
    if (['move-down', 'move-up', 'move-left', 'move-right'].includes(action)) {
      const tempWidgets = cloneDeep(widgetList);
      const findIndex = tempWidgets.findIndex((w) => w._id === value);
      if (findIndex > -1) {
        if (action === 'move-down' || action === 'move-up')
          tempWidgets[findIndex].startRow =
            tempWidgets[findIndex].startRow + (action === 'move-down' ? 1 : -1);
        else if (action === 'move-left' || action === 'move-right')
          tempWidgets[findIndex].startColumn =
            tempWidgets[findIndex].startColumn + (action === 'move-left' ? -1 : 1);

        setWidgetList(tempWidgets);
      }
    } else if (action === 'delete' || action === 'duplicate') {
      setSelectedWidgetId(value);
      setConfirmType(action);
    } else if (action === 'edit') {
      setSelectedWidgetId(value);
      setShowEditModal(true);
    }
  };

  useEffect(() => {
    if (selectedPageId) getPageDetails();
  }, [selectedPageId]);

  return (
    <>
      <div
        className='widgets-grid-view'
        style={{ '--columns': DEFAULT_NUMBER_OF_COLUMNS, '--rows': currentWidgetsRows }}
      >
        {Array.from({
          length: DEFAULT_NUMBER_OF_COLUMNS * currentWidgetsRows - totalOccupiedCells,
        }).map((_, i) => (
          <div key={`cell-${i}`} className='grid-cell theme-background-grey-15' />
        ))}
        {widgetList.map((widget, index) => (
          <div
            key={index}
            className='widget'
            style={{
              gridColumn: `${widget.startColumn} / span ${widget.takeColumns}`,
              gridRow: `${widget.startRow} / span ${widget.takeRows}`,
            }}
          >
            <ChartEditHeader
              widget={widget}
              handleAction={handleAction}
              currentWidgetsRows={currentWidgetsRows}
            />
            {widget.basicProps.chartType === 'statsCard' ? (
              <StatsChartComponent widget={widget} data={chartTempData.statsCard} />
            ) : widget.basicProps.chartType === 'textCard' ? (
              <TextChartComponent widget={widget} editMode />
            ) : widget.basicProps.chartType === 'imageCard' ? (
              <ImageChartComponent widget={widget} />
            ) : widget.basicProps.chartType === 'lineChart' ? (
              <LineChartComponent widget={widget} stats={chartTempData.lineChart} />
            ) : widget.basicProps.chartType === 'barChartHorizontal' ||
              widget.basicProps.chartType === 'barChartVertical' ||
              widget.basicProps.chartType === 'stackedBarChartHorizontal' ||
              widget.basicProps.chartType === 'stackedBarChartVertical' ? (
              <BarChartComponent
                widget={widget}
                stats={chartTempData[widget.basicProps.chartType]}
                isVertical={
                  widget.basicProps.chartType === 'barChartVertical' ||
                  widget.basicProps.chartType === 'stackedBarChartVertical'
                }
              />
            ) : widget.basicProps.chartType === 'heatMapTable' ? (
              <HeatMapChartComponent widget={widget} stats={chartTempData.heatMapTable} />
            ) : widget.basicProps.chartType === 'dialChart' ? (
              <DialChartComponent
                widget={widget}
                stats={chartTempData.dialChart}
                chartType={widget.basicProps.chartType}
              />
            ) : widget.basicProps.chartType === 'pieChart' ||
              widget.basicProps.chartType === 'doughnutChart' ? (
              <PieChartComponent
                widget={widget}
                stats={
                  widget.basicProps.chartType === 'pieChart'
                    ? chartTempData.pieChart
                    : chartTempData.doughnutChart
                }
                chartType={widget.basicProps.chartType}
              />
            ) : (
              <div>
                <label>{widget.basicProps.chartType}</label>
              </div>
            )}
          </div>
        ))}
      </div>
      {!!confirmType && (
        <ConfirmationModal
          show={!!confirmType}
          title={t(
            confirmType === 'delete'
              ? 'alert_dashboard_page_widget_deletion_title'
              : 'alert_dashboard_page_widget_duplication_title',
          )}
          message={t(
            confirmType === 'delete'
              ? 'alert_dashboard_page_widget_deletion_message'
              : 'alert_dashboard_page_widget_duplication_message',
          )}
          actionList={[
            {
              color: 'black-1',
              text: t('button_cancel'),
              onClick: () => {
                setConfirmType(null);
                setSelectedWidgetId(null);
              },
            },
            confirmType === 'delete'
              ? { color: 'red-1', text: t('button_delete'), onClick: () => handleDeleteWidget() }
              : {
                  color: 'black-1',
                  text: t('button_duplicate'),
                  onClick: () => handleDuplicateWidget(),
                },
          ]}
        />
      )}

      {showEditModal && (
        <DashboardAddWidgetModal
          editMode={true}
          widgetData={widgetList.find((w) => w._id === selectedWidgetId)}
          handleClose={() => {
            setShowEditModal(false);
            setSelectedWidgetId(null);
          }}
          pageId={selectedPageId}
          dashboardId={dashboardId}
          currentWidgetsRows={0}
          getPageDetails={getPageDetails}
          setSaveLoader={setSaveLoader}
        />
      )}
    </>
  );
};

PageWidgetsEditView.propTypes = {
  selectedPageId: PropTypes.string,
  getPageDetails: PropTypes.func.isRequired,
  currentWidgetsRows: PropTypes.number.isRequired,
  widgetList: PropTypes.array.isRequired,
  setWidgetList: PropTypes.func.isRequired,
  dashboardId: PropTypes.string,
  setSaveLoader: PropTypes.func.isRequired,
};

export default PageWidgetsEditView;
